import { graphHooks } from '@tunasong/graph-lib/react'
import { canWriteEntity, logger } from '@tunasong/models'
import { entitiesApi, useCurrentUser } from '@tunasong/redux'
import type { Agent, Entity, Persisted } from '@tunasong/schemas'
import { isAgent } from '@tunasong/schemas'
import { useActionToast } from '@tunasong/ui-lib'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { getAgentDisplayName } from '../agent-visuals.js'
import { personaChangedSince } from '../persona-changed.js'
import { personalizeCopy } from './personalize-copy.js'
import { PersonalizeDialog } from './personalize-dialog.js'
import {
  defaultNameOf,
  formOf,
  writeFromAgent,
  writeOf,
  type PersonaForm,
  type PersonaWrite,
} from './personalize-form.js'
import { scopeLineOf } from './personalize-scope.js'

interface OpenState {
  /** The agent as it stood when the dialog opened, or after "Load theirs": the conflict check compares to it. */
  opened: Persisted<Agent>
  formKey: string
  conflict: Persisted<Agent> | undefined
}

const statusOf = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error ? (error as { status: unknown }).status : undefined

/**
 * Personalize an agent (Joni's UX spec §1–§3): who may, the dialog, and an honest save.
 *
 * Save re-reads the agent and compares only the three persona fields with what the dialog opened on
 * (`personaChangedSince`): the agent's own runtime writes never raise a conflict. A conflict asks; it never
 * overwrites silently. A saved change offers Undo for 6 s; a failed one says nothing changed, keeps the
 * dialog and the band's text, and offers Retry.
 *
 * Render `dialog` and `toast` wherever the button lives.
 */
export const usePersonalizeAgent = (agent: Persisted<Agent>) => {
  const { userId } = useCurrentUser()
  const ancestors = graphHooks.useAncestors(agent)
  const [loadAgent] = entitiesApi.useLazyLoadEntityQuery()
  const [updateEntity] = entitiesApi.useUpdateEntityMutation()
  const { showToast, toast } = useActionToast()
  const [open, setOpen] = useState<OpenState | null>(null)
  const [saving, setSaving] = useState(false)

  const name = getAgentDisplayName(agent)
  const canPersonalize = canWriteEntity(userId, agent)

  const write = async (persona: PersonaWrite) =>
    updateEntity({ id: agent.id, partialEntity: persona as Partial<Entity>, eTag: null }).unwrap()

  const readFresh = async (): Promise<Persisted<Agent> | 'removed'> => {
    try {
      const fresh = await loadAgent({ id: agent.id }, false).unwrap()
      return isAgent(fresh) ? (fresh as Persisted<Agent>) : 'removed'
    } catch (error) {
      if (statusOf(error) === 404) {
        return 'removed'
      }
      throw error
    }
  }

  const save = async (form: PersonaForm, options: { overwrite?: boolean } = {}) => {
    if (!open) {
      return
    }
    setSaving(true)
    try {
      const fresh = await readFresh()
      if (fresh === 'removed') {
        showToast({ message: personalizeCopy.removed(name) })
        setOpen(null)
        return
      }
      if (!options.overwrite && personaChangedSince(open.opened, fresh).length > 0) {
        setOpen({ ...open, conflict: fresh })
        return
      }
      const previous = writeFromAgent(fresh)
      const next = writeOf(form, agent)
      await write(next)
      setOpen(null)
      showToast({
        message: personalizeCopy.saved(next.name),
        actionLabel: personalizeCopy.undo,
        duration: 6000,
        onAction: () => {
          write(previous).catch(error => logger.warn('Personalize: undo failed', { agentId: agent.id, error }))
        },
      })
    } catch (error) {
      logger.warn('Personalize: save failed', { agentId: agent.id, error })
      // The dialog stays open with the band's text; nothing they typed is lost (Joni §3)
      showToast({
        message: personalizeCopy.failed(name),
        actionLabel: personalizeCopy.retry,
        onAction: () => {
          void save(form, options)
        },
      })
    } finally {
      setSaving(false)
    }
  }

  const openDialog = () => setOpen({ opened: agent, formKey: `${agent.id}-${Date.now()}`, conflict: undefined })

  const handleLoadTheirs = () => {
    if (open?.conflict) {
      setOpen({ opened: open.conflict, formKey: `${agent.id}-${Date.now()}`, conflict: undefined })
    }
  }

  const shown = open?.opened ?? agent
  const dialog: ReactNode = (
    <PersonalizeDialog
      open={open !== null}
      agentName={name}
      defaultName={defaultNameOf(agent)}
      initial={formOf(shown)}
      formKey={open?.formKey ?? 'closed'}
      scopeLine={scopeLineOf(agent, ancestors, name)}
      saving={saving}
      conflict={open?.conflict !== undefined}
      onCancel={() => setOpen(null)}
      onSave={form => void save(form)}
      onUseMine={form => void save(form, { overwrite: true })}
      onLoadTheirs={handleLoadTheirs}
    />
  )

  return { canPersonalize, openDialog, dialog, toast, name }
}
